import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Search, Pencil, Save, X, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProcessingMasterPage() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [dragging, setDragging] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ dhoti_category: "", processing_mtr_rate: "", folding_rate_per_dhoti: "" });
  const [addLoading, setAddLoading] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/processing-master");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith(".csv")) { toast.error("Please upload a CSV file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/processing-master/upload-csv", formData);
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); handleUpload(e.dataTransfer.files[0]); };

  const handleAdd = async () => {
    if (!addForm.dhoti_category) { toast.error("Category is required"); return; }
    setAddLoading(true);
    try {
      await api.post("/processing-master", {
        dhoti_category: addForm.dhoti_category,
        processing_mtr_rate: parseFloat(addForm.processing_mtr_rate) || 0,
        folding_rate_per_dhoti: parseFloat(addForm.folding_rate_per_dhoti) || 0,
      });
      toast.success("Category added"); setAddOpen(false);
      setAddForm({ dhoti_category: "", processing_mtr_rate: "", folding_rate_per_dhoti: "" });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddLoading(false); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValues({
      dhoti_category: item.dhoti_category,
      processing_mtr_rate: item.processing_mtr_rate,
      folding_rate_per_dhoti: item.folding_rate_per_dhoti,
    });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/processing-master/${id}`, editValues);
      toast.success("Updated"); setEditingId(null); fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/processing-master/${id}`);
      toast.success("Deleted"); fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = data.filter((item) =>
    item.dhoti_category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="processing-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Processing & Folding Master</h1>
          <p className="text-sm text-stone-500 mt-1">Manage processing and folding rates. Upload CSV to merge/update or add manually.</p>
        </div>
        <Button data-testid="add-processing-button" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />Add Category
        </Button>
      </div>

      {/* CSV Upload */}
      <div
        className={`csv-dropzone ${dragging ? "dragging" : ""}`}
        data-testid="processing-csv-upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="processing-csv-file-input"
          onChange={(e) => handleUpload(e.target.files[0])} />
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#C2410C]" />
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto text-stone-400 mb-2" strokeWidth={1.5} />
            <p className="text-sm font-medium text-stone-700">Drop CSV to merge/update existing data</p>
            <p className="text-xs text-stone-400 mt-1">Columns: Dhoti Category for Processing, Processing Mtr Rate, Folding Rate / Dhoti</p>
          </>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <Input data-testid="processing-search-input" placeholder="Search by category..." value={search}
          onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" />
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Dhoti Category</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Processing Rate/Mtr</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Folding Rate/Dhoti</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-stone-400">
                {data.length === 0 ? "No data. Upload a CSV to get started." : "No matching records."}
              </TableCell></TableRow>
            ) : (
              filtered.map((item, idx) => (
                <TableRow key={item.id} className="animate-row hover:bg-stone-50/50" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`processing-row-${item.id}`}>
                  <TableCell className="px-4 py-2.5 text-sm font-medium text-stone-900">
                    {editingId === item.id ? (
                      <Input value={editValues.dhoti_category} onChange={(e) => setEditValues((v) => ({ ...v, dhoti_category: e.target.value }))} className="h-8 w-52" />
                    ) : item.dhoti_category}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right">
                    {editingId === item.id ? (
                      <Input type="number" step="0.01" value={editValues.processing_mtr_rate} onChange={(e) => setEditValues((v) => ({ ...v, processing_mtr_rate: parseFloat(e.target.value) || 0 }))} className="h-8 w-24 text-right ml-auto" />
                    ) : <span className="font-mono">{item.processing_mtr_rate?.toFixed(2)}</span>}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right">
                    {editingId === item.id ? (
                      <Input type="number" step="0.01" value={editValues.folding_rate_per_dhoti} onChange={(e) => setEditValues((v) => ({ ...v, folding_rate_per_dhoti: parseFloat(e.target.value) || 0 }))} className="h-8 w-24 text-right ml-auto" />
                    ) : <span className="font-mono">{item.folding_rate_per_dhoti?.toFixed(2)}</span>}
                  </TableCell>
                  <TableCell className="px-4 py-2.5">
                    {editingId === item.id ? (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" data-testid="save-processing-button" onClick={() => saveEdit(item.id)} className="h-7 w-7 text-green-600 hover:bg-green-50"><Save className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} className="h-7 w-7 text-stone-500 hover:bg-stone-100"><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" data-testid="edit-processing-button" onClick={() => startEdit(item)} className="h-7 w-7 text-stone-500 hover:text-[#C2410C] hover:bg-orange-50"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" data-testid="delete-processing-button" onClick={() => deleteItem(item.id)} className="h-7 w-7 text-stone-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data.length > 0 && <p className="text-xs text-stone-400">Showing {filtered.length} of {data.length} records</p>}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Add Category</DialogTitle><DialogDescription>Enter processing and folding details.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-sm font-medium mb-1.5 block">Dhoti Category *</Label><Input placeholder="e.g. Double Dhoti White" value={addForm.dhoti_category} onChange={(e) => setAddForm((f) => ({ ...f, dhoti_category: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Processing Rate/Mtr</Label><Input type="number" step="0.01" value={addForm.processing_mtr_rate} onChange={(e) => setAddForm((f) => ({ ...f, processing_mtr_rate: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Folding Rate/Dhoti</Label><Input type="number" step="0.01" value={addForm.folding_rate_per_dhoti} onChange={(e) => setAddForm((f) => ({ ...f, folding_rate_per_dhoti: e.target.value }))} /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={handleAdd} disabled={addLoading}>
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{addLoading ? "Adding..." : "Add Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
